import React, { useState, useEffect } from 'react';
import { 
  UserPlus, 
  Gamepad2, 
  Trophy, 
  DollarSign, 
  ArrowRight,
  CheckCircle,
  Play,
  Users
} from 'lucide-react';
import '../styles/process.css';
import { convertToLocalCurrency } from '../../../../utils/currencyUtils';

function ProcessSection() {
  const [prizePool, setPrizePool] = useState('$7.5K+');

  const steps = [
    {
      icon: <UserPlus size={24} />,
      title: 'Create Account',
      description: 'Sign up with your email and create your gaming profile in minutes.',
      color: '#FFD700',
      step: '01'
    },
    {
      icon: <Gamepad2 size={24} />,
      title: 'Choose Your Game',
      description: 'Browse and select from our supported games like FIFA, Call of Duty, and more.',
      color: '#ff0000',
      step: '02'
    },
    {
      icon: <Trophy size={24} />,
      title: 'Join or Host Tournament',
      description: 'Participate in existing tournaments or create your own with custom rules.',
      color: '#FFD700',
      step: '03'
    },
    {
      icon: <DollarSign size={24} />,
      title: 'Earn Rewards',
      description: 'Win prizes, earn money, and build your reputation in the gaming community.',
      color: '#ff0000',
      step: '04'
    }
  ];

  const features = [
    {
      icon: <CheckCircle size={20} />,
      text: 'Free to join tournaments'
    },
    {
      icon: <CheckCircle size={20} />,
      text: 'Instant prize distribution'
    },
    {
      icon: <CheckCircle size={20} />,
      text: 'Live streaming support'
    },
    {
      icon: <CheckCircle size={20} />,
      text: 'Community features'
    },
    {
      icon: <CheckCircle size={20} />,
      text: 'Mobile app coming soon'
    },
    {
      icon: <CheckCircle size={20} />,
      text: '24/7 customer support'
    }
  ];

  useEffect(() => {
    // Convert $7.5K USD to local currency
    const localCurrency = convertToLocalCurrency(7500);
    setPrizePool(localCurrency.formatted);
  }, []);

  return (
    <section className="process-section">
      <div className="process-container">
        {/* Section Header */}
        <div className="process-header">
          <div className="process-badge">
            <Play size={16} />
            <span>How It Works</span>
          </div>
          <h2 className="process-title">
            Get Started in 4 Simple Steps
          </h2>
          <p className="process-subtitle">
            Join thousands of gamers who are already earning while playing their favorite games.
          </p>
        </div>

        {/* Process Steps */}
        <div className="process-steps">
          {steps.map((step, index) => (
            <div 
              key={index} 
              className="process-step"
              style={{ '--step-color': step.color } as React.CSSProperties}
            >
              <div className="step-number">{step.step}</div>
              <div className="step-icon">
                {step.icon}
              </div>
              <h3 className="step-title">{step.title}</h3>
              <p className="step-description">{step.description}</p>
              
              {/* Connector Line */}
              {index < steps.length - 1 && (
                <div className="step-connector">
                  <ArrowRight size={20} />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Features List */}
        <div className="process-features">
          <div className="features-header">
            <h3 className="features-title">Why Choose Cheda Gaming?</h3>
            <p className="features-subtitle">
              Everything you need to succeed in competitive gaming
            </p>
          </div>
          
          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-item">
                <div className="feature-icon">
                  {feature.icon}
                </div>
                <span className="feature-text">{feature.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Stats */}
        <div className="process-stats">
          <div className="stat-card">
            <Users size={32} />
            <div className="stat-content">
              <span className="stat-number">1.5K+</span>
              <span className="stat-label">Active Players</span>
            </div>
          </div>
          
          <div className="stat-card">
            <Trophy size={32} />
            <div className="stat-content">
              <span className="stat-number">500+</span>
              <span className="stat-label">Tournaments</span>
            </div>
          </div>
          
          <div className="stat-card">
            <DollarSign size={32} />
            <div className="stat-content">
              <span className="stat-number">{prizePool}</span>
              <span className="stat-label">Prize Pool</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default ProcessSection; 